<template>
  <section ref="videoPlayerRef" class="w-full">
    <video
      v-if="src"
      ref="videoRef"
      class="video-js custom-video"
      :class="{ 'h-screen': fullscreen }"
      :poster="poster"
      webkit-playsinline
      playsinline
      x5-video-player-type="h5"
    >
      <source :src="src" />
    </video>
  </section>
</template>

<script setup lang="ts">
import { v4 as uuidv4 } from 'uuid';
import Player from 'video.js/dist/types/player';
import { initVideojs } from '@/utils/initVideojs';
import 'video.js/dist/video-js.min.css';

defineOptions({ name: 'VideoPlayer' });
const props = withDefaults(
  defineProps<{
    src: string;
    poster?: string;
    autoplay?: boolean;
    loop?: boolean;
    controls?: boolean;
    muted?: boolean;
    aspectRatio?: string;
    fluid?: boolean;
    fullscreen?: boolean;
    preload?: 'auto' | 'metadata' | 'none';
    width?: number | string;
    height?: number | string;
  }>(),
  {
    poster: import.meta.env.VITE_DEFAULT_IMAGE_16_9,
    autoplay: false,
    muted: false,
    loop: false,
    controls: true,
    aspectRatio: '',
    fluid: false,
    fullscreen: false,
    preload: 'metadata',
    width: '',
    height: '',
  },
);
const videoPlayerRef = ref();
const { width: elementWidth } = useElementSize(videoPlayerRef);
const videoWidth = computed(() => {
  return `${elementWidth.value}px`;
});
const videoRef = ref();
const uuid = uuidv4();
const playingUUID = inject<Ref<string>>('playingUUID', ref(''));
const videoPlayer = ref<Player>();
interface VideoJsOtherOptions {
  fluid?: boolean;
  aspectRatio?: string;
  width?: number | string;
  height?: number | string;
}

watch(
  () => props.src,
  (newVal: string) => {
    if (newVal) {
      if (videoPlayer.value) {
        videoPlayer.value.src(newVal);
      } else {
        nextTick(() => {
          const videoJsOtherOptions: VideoJsOtherOptions = {};
          if (!props.fullscreen && props.aspectRatio && props.aspectRatio.includes(':')) {
            videoJsOtherOptions.aspectRatio = props.aspectRatio;
          } else if (!props.fullscreen && props.fluid) {
            videoJsOtherOptions.fluid = props.fluid;
          }
          if (props.width) {
            videoJsOtherOptions.width = props.width;
          }
          if (props.height) {
            videoJsOtherOptions.height = props.height;
          }
          const videojs = initVideojs();
          videoPlayer.value = videojs(videoRef.value, {
            autoplay: props.autoplay,
            controls: props.controls,
            poster: props.poster,
            muted: props.muted,
            loop: props.loop,
            preload: props.preload,
            language: 'zh-cn',
            ...videoJsOtherOptions,
          });
          videoPlayer.value.on('play', () => {
            playingUUID.value = uuid;
          });
        });
      }
    }
  },
  {
    immediate: true,
  },
);
watch(
  () => playingUUID.value,
  (val) => {
    if (val !== uuid && videoPlayer.value) {
      if (!videoPlayer.value.paused()) {
        videoPlayer.value.pause();
      }
    }
  },
);
defineExpose({ videoPlayer });
</script>

<style lang="scss" scoped>
.custom-video {
  width: v-bind('videoWidth');
}
</style>
